import {
  Button,
  Card,
  CardContent,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  EmptyState,
  Input,
  Label,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import type { RateLimitEntity, RateLimitInputType } from '../api/rateLimits.api';
import { rateLimitsApi } from '../api/rateLimits.api';
import { isManagedByCac, ManagedByCacBadge } from '../components/shared/ManagedByCac';
import { CHANNEL_OPTIONS } from '../constants/channel-colors';

const EMPTY_FORM: RateLimitInputType = {
  tenantId: null,
  userId: null,
  channel: null,
  softLimit: 100,
  hardLimit: 200,
  windowSeconds: 60,
  action: 'reject',
};

interface RateLimitFormProps {
  initial: RateLimitInputType;
  onSubmit: (data: RateLimitInputType) => void;
  onCancel: () => void;
  loading: boolean;
  submitLabel: string;
}

function RateLimitForm({ initial, onSubmit, onCancel, loading, submitLabel }: RateLimitFormProps) {
  const [form, setForm] = useState<RateLimitInputType>(initial);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="rl-tenant">Tenant ID</Label>
              <Input
                id="rl-tenant"
                value={form.tenantId ?? ''}
                onChange={(e) => setForm({ ...form, tenantId: e.target.value || null })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-user">User ID</Label>
              <Input
                id="rl-user"
                value={form.userId ?? ''}
                onChange={(e) => setForm({ ...form, userId: e.target.value || null })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-channel">Channel</Label>
              <Select
                value={form.channel ?? 'all'}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    channel: (v === 'all' ? null : v) as RateLimitInputType['channel'],
                  })
                }
              >
                <SelectTrigger id="rl-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  {CHANNEL_OPTIONS.map((ch) => (
                    <SelectItem key={ch} value={ch}>
                      {ch}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-action">Action</Label>
              <Select
                value={form.action ?? 'reject'}
                onValueChange={(v) => setForm({ ...form, action: v as 'queue' | 'reject' })}
              >
                <SelectTrigger id="rl-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reject">Reject</SelectItem>
                  <SelectItem value="queue">Queue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-soft">Soft limit *</Label>
              <Input
                id="rl-soft"
                type="number"
                min={1}
                value={form.softLimit}
                onChange={(e) => setForm({ ...form, softLimit: parseInt(e.target.value, 10) || 0 })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-hard">Hard limit *</Label>
              <Input
                id="rl-hard"
                type="number"
                min={1}
                value={form.hardLimit}
                onChange={(e) => setForm({ ...form, hardLimit: parseInt(e.target.value, 10) || 0 })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-window">Window (seconds)</Label>
              <Input
                id="rl-window"
                type="number"
                min={1}
                value={form.windowSeconds ?? 60}
                onChange={(e) =>
                  setForm({ ...form, windowSeconds: parseInt(e.target.value, 10) || 60 })
                }
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={loading || form.softLimit <= 0 || form.hardLimit <= 0}>
              {loading ? 'Saving…' : submitLabel}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function RateLimitsPage() {
  const { data: items, loading, error, refetch } = rateLimitsApi.useList();
  const { mutate: createItem, loading: creating } = rateLimitsApi.useCreate();
  const { mutate: updateItem, loading: updating } = rateLimitsApi.useUpdate();
  const { mutate: deleteItem } = rateLimitsApi.useDelete();
  const { showSnackbar } = useSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleCreate = async (data: RateLimitInputType) => {
    try {
      await createItem(data);
      showSnackbar({ message: 'Rate limit created', variant: 'success' });
      setShowCreate(false);
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to create rate limit', variant: 'error' });
    }
  };

  const handleUpdate = async (id: string, data: RateLimitInputType) => {
    try {
      await updateItem(id, data);
      showSnackbar({ message: 'Rate limit updated', variant: 'success' });
      setEditingId(null);
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to update rate limit', variant: 'error' });
    }
  };

  const toInput = (item: RateLimitEntity): RateLimitInputType => ({
    tenantId: item.tenantId ?? null,
    userId: item.userId ?? null,
    channel: item.channel ?? null,
    softLimit: item.softLimit,
    hardLimit: item.hardLimit,
    windowSeconds: item.windowSeconds,
    action: item.action as 'queue' | 'reject',
  });

  if (error) {
    return (
      <ListPage title="Rate Limits" description="Manage per-tenant and per-app rate limit rules.">
        <EmptyState
          title="Couldn't load rate limits"
          description={error instanceof Error ? error.message : 'Try again in a moment.'}
          action={<Button onClick={() => refetch()}>Retry</Button>}
          className="border-destructive/40 bg-destructive/5"
        />
      </ListPage>
    );
  }

  const list = (items ?? []) as RateLimitEntity[];

  return (
    <ListPage
      title="Rate Limits"
      description="Define per-tenant and per-app sliding-window rate limits."
      actions={
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={refetch} isRefreshing={loading} />
          <Button
            onClick={() => {
              setShowCreate(true);
              setEditingId(null);
            }}
          >
            <Plus /> Create rule
          </Button>
        </div>
      }
    >
      {showCreate ? (
        <RateLimitForm
          initial={EMPTY_FORM}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          loading={creating}
          submitLabel="Create"
        />
      ) : null}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Scope</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead className="text-right">Soft</TableHead>
              <TableHead className="text-right">Hard</TableHead>
              <TableHead className="text-right">Window</TableHead>
              <TableHead>Action</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={`s-${i}-${j}`}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    title="No rate limits configured"
                    description="Create a rule to control notification throughput per tenant, user, or channel."
                    action={
                      <Button onClick={() => setShowCreate(true)}>
                        <Plus /> Create rule
                      </Button>
                    }
                    className="border-0 bg-transparent"
                  />
                </TableCell>
              </TableRow>
            ) : (
              list.flatMap((item) => {
                if (editingId === item.id) {
                  return [
                    <TableRow key={`edit-${item.id}`}>
                      <TableCell colSpan={7} className="p-0">
                        <RateLimitForm
                          initial={toInput(item)}
                          onSubmit={(data) => handleUpdate(item.id, data)}
                          onCancel={() => setEditingId(null)}
                          loading={updating}
                          submitLabel="Save"
                        />
                      </TableCell>
                    </TableRow>,
                  ];
                }

                const scope =
                  [
                    item.tenantId && `T: ${item.tenantId.slice(0, 8)}…`,
                    item.userId && `U: ${item.userId.slice(0, 8)}…`,
                  ]
                    .filter(Boolean)
                    .join(', ') || 'App-wide';

                return [
                  <TableRow key={item.id}>
                    <TableCell className="text-sm">{scope}</TableCell>
                    <TableCell>
                      {item.channel ? (
                        <ChannelBadge channel={item.channel as ChannelKind} />
                      ) : (
                        <span className="text-xs text-muted-foreground">All</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{item.softLimit}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.hardLimit}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {item.windowSeconds}s
                    </TableCell>
                    <TableCell>
                      <StatusBadge tone={item.action === 'reject' ? 'danger' : 'warning'}>
                        {item.action}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isManagedByCac(item) ? (
                        <ManagedByCacBadge />
                      ) : (
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingId(item.id);
                              setShowCreate(false);
                            }}
                          >
                            <Pencil /> Edit
                          </Button>
                          <ConfirmAction
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 /> Delete
                              </Button>
                            }
                            title="Delete this rate limit?"
                            description="This action cannot be undone. Traffic will revert to the next-applicable rule (or unlimited if none)."
                            confirmLabel="Delete"
                            destructive
                            onConfirm={async () => {
                              await deleteItem(item.id);
                              refetch();
                            }}
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>,
                ];
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </ListPage>
  );
}
